import React, { useState, useEffect } from 'react';
import { X, Loader2, Copy, CheckCircle, AlertCircle, Share2, Download, BookOpen, FileText } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { generateSermonSummary } from '../lib/openai';

interface SeriesShareModalProps {
  seriesId: string;
  seriesTitle: string;
  seriesDescription: string | null;
  sermons: Array<{
    id: string;
    title: string;
    scripture: string;
    content: string;
    date: string | null;
    youtube_url?: string;
  }>;
  onClose: () => void;
}

export function SeriesShareModal({
  seriesId,
  seriesTitle,
  seriesDescription,
  sermons,
  onClose
}: SeriesShareModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [step, setStep] = useState<'initial' | 'generating' | 'complete'>('initial');

  useEffect(() => {
    const checkExistingShare = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('shared_series')
          .select('id, summary')
          .eq('series_id', seriesId)
          .maybeSingle();

        if (fetchError) throw fetchError;

        if (data) {
          const shareUrl = `${window.location.origin}/series/${data.id}`;
          setShareUrl(shareUrl);
          setStep('complete');

          if (data.summary) {
            setSummary(data.summary);
          }
        }
      } catch (err) {
        console.error('Error checking existing share:', err);
      }
    };

    checkExistingShare();
  }, [seriesId]);

  const generateSeriesSummary = async () => {
    if (!sermons.length) return null;

    try {
      setGeneratingSummary(true);

      // Combine all sermon content for analysis
      const combinedContent = sermons.map(sermon => {
        const textContent = new DOMParser().parseFromString(sermon.content, 'text/html').body.textContent ?? '';
        return `Sermon: ${sermon.title}\nScripture: ${sermon.scripture}\nContent: ${textContent.slice(0, 2000)}`;
      }).join('\n\n---\n\n');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session found');

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          type: 'enhance',
          content: `Series Title: ${seriesTitle}
Series Description: ${seriesDescription || 'No description provided'}
Number of Sermons: ${sermons.length}

Sermon Content:
${combinedContent}

Create a comprehensive summary of this sermon series that includes:
1. The main themes and topics covered across all sermons
2. The overarching message or purpose of the series
3. Key takeaways that connect the sermons together
4. How the series progresses or builds upon itself

Format your response as a well-structured summary of about 200-300 words.`,
          enhancementType: 'series'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate series summary');
      }

      const data = await response.json();

      if (!data.success || !data.content) {
        throw new Error('No summary content received');
      }

      return data.content;
    } catch (error) {
      console.error('Error generating series summary:', error);
      // Fallback summary
      return `This ${sermons.length}-part sermon series "${seriesTitle}" explores key biblical themes and practical applications for Christian living. Each sermon builds upon the previous message to create a comprehensive study that deepens understanding and encourages spiritual growth.`;
    } finally {
      setGeneratingSummary(false);
    }
  };

  const handleGenerateSharePage = async () => {
    setStep('generating');
    setLoading(true);
    setError(null);

    try {
      // Generate series summary
      const seriesSummary = await generateSeriesSummary();
      setSummary(seriesSummary);

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      // Check for existing share
      const { data: existingShare, error: selectError } = await supabase
        .from('shared_series')
        .select('id')
        .eq('series_id', seriesId)
        .maybeSingle();

      if (selectError) throw selectError;

      let sharedSeries;

      if (existingShare) {
        // Update existing share
        const { data: updatedShare, error: updateError } = await supabase
          .from('shared_series')
          .update({
            summary: seriesSummary
          })
          .eq('id', existingShare.id)
          .select()
          .single();

        if (updateError) throw updateError;
        sharedSeries = updatedShare;
      } else {
        // Create new share
        const { data: newShare, error: insertError } = await supabase
          .from('shared_series')
          .insert({
            user_id: user.id,
            series_id: seriesId,
            summary: seriesSummary
          })
          .select()
          .single();

        if (insertError) throw insertError;
        sharedSeries = newShare;
      }

      if (!sharedSeries) throw new Error('Failed to create or update shared series');

      const shareUrl = `${window.location.origin}/shared/series/${sharedSeries.id}`;
      setShareUrl(shareUrl);
      setStep('complete');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error generating share page:', err);
      setError(errorMessage);
      setStep('initial');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      setError('Failed to copy link to clipboard');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-6 border-b border-sand/20 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-midnight">Create Series Share Page</h2>
          <button
            onClick={onClose}
            className="p-1 text-slate hover:text-midnight rounded-full"
            type="button"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="bg-crimson/10 text-crimson p-4 rounded-lg mb-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Error generating share page</p>
                  <p className="text-sm mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {step === 'initial' && (
            <div className="space-y-6">
              {shareUrl ? (
                <div className="bg-emerald/10 p-4 rounded-lg">
                  <h3 className="text-lg font-medium text-midnight mb-2">Series Share Page Already Exists</h3>
                  <p className="text-slate mb-4">
                    This series already has a shareable page. You can copy the link below or view the page directly.
                  </p>

                  <div className="flex items-center gap-2 bg-white p-2 rounded-lg border border-sand/20 mb-4">
                    <input
                      type="text"
                      value={shareUrl}
                      readOnly
                      className="flex-1 px-3 py-2 bg-transparent focus:outline-none text-midnight"
                    />
                    <button
                      onClick={handleCopyLink}
                      className="px-3 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 flex items-center"
                      type="button"
                    >
                      {copied ? (
                        <>
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>

                  <div className="flex justify-center space-x-4">
                    <a
                      href={shareUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-midnight text-white rounded-lg hover:bg-midnight/90"
                    >
                      View Page
                    </a>
                    <button
                      onClick={() => {
                        setStep('initial');
                        setShareUrl(null);
                        setSummary(null);
                      }}
                      className="px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90"
                    >
                      Regenerate Page
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="bg-sand/10 p-4 rounded-lg">
                    <h3 className="text-lg font-medium text-midnight mb-2">Create a Shareable Series Page</h3>
                    <p className="text-slate mb-4">
                      This will create a public page with your sermon series content, an AI-generated summary, 
                      and links to all sermons and study materials.
                    </p>

                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-electric/10 p-2 rounded-lg">
                          <BookOpen className="h-5 w-5 text-electric" />
                        </div>
                        <div>
                          <h4 className="font-medium text-midnight">Series Overview</h4>
                          <p className="text-sm text-slate">AI-generated summary of the entire series</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-emerald/10 p-2 rounded-lg">
                          <FileText className="h-5 w-5 text-emerald" />
                        </div>
                        <div>
                          <h4 className="font-medium text-midnight">All Sermons</h4>
                          <p className="text-sm text-slate">Access to all {sermons.length} sermons in the series</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-gold/10 p-2 rounded-lg">
                          <Download className="h-5 w-5 text-gold" />
                        </div>
                        <div>
                          <h4 className="font-medium text-midnight">Study Resources</h4>
                          <p className="text-sm text-slate">Download study guides for each sermon</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-crimson/10 p-2 rounded-lg">
                          <Share2 className="h-5 w-5 text-crimson" />
                        </div>
                        <div>
                          <h4 className="font-medium text-midnight">Social Sharing</h4>
                          <p className="text-sm text-slate">Easy sharing options for social media</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-4">
                    <button
                      onClick={onClose}
                      className="px-4 py-2 text-slate hover:text-midnight"
                      type="button"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleGenerateSharePage}
                      className="px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90"
                      type="button"
                    >
                      Generate Series Page
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {step === 'generating' && (
            <div className="space-y-6">
              <div className="text-center py-6">
                <h3 className="text-lg font-medium text-midnight mb-4">Generating Your Series Page</h3>
                <div className="space-y-6">
                  <div className="flex items-center justify-center gap-4">
                    <Loader2 className="h-8 w-8 animate-spin text-electric" />
                    <span className="text-slate">Creating series summary and page...</span>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${!generatingSummary ? 'bg-emerald/10 text-emerald' : 'bg-sand/10 text-slate'}`}>
                        {!generatingSummary ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        )}
                      </div>
                      <span className="text-midnight">Generate Series Summary</span>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${step === 'complete' ? 'bg-emerald/10 text-emerald' : 'bg-sand/10 text-slate'}`}>
                        {step === 'complete' ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        )}
                      </div>
                      <span className="text-midnight">Create Share Page</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 'complete' && shareUrl && (
            <div className="space-y-6">
              <div className="bg-emerald/10 p-6 rounded-lg text-center">
                <CheckCircle className="h-12 w-12 text-emerald mx-auto mb-4" />
                <h3 className="text-lg font-medium text-midnight mb-2">Series Share Page Created!</h3>
                <p className="text-slate mb-6">
                  Your series share page has been created and is ready to share.
                </p>

                <div className="flex items-center gap-2 bg-white p-2 rounded-lg border border-sand/20 mb-4">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 px-3 py-2 bg-transparent focus:outline-none text-midnight"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="px-3 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 flex items-center"
                    type="button"
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-1" />
                        Copy
                      </>
                    )}
                  </button>
                </div>

                <div className="flex justify-center space-x-4">
                  <a
                    href={shareUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-midnight text-white rounded-lg hover:bg-midnight/90"
                  >
                    View Page
                  </a>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => {
                    setStep('initial');
                    setShareUrl(null);
                    setSummary(null);
                  }}
                  className="px-4 py-2 bg-sand/20 text-slate rounded-lg hover:bg-sand/30"
                  type="button"
                >
                  Regenerate Page
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-slate hover:text-midnight"
                  type="button"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}